/**
 * 拾取插件：注册 PickingService 服务，提供统一拾取接口与注册点
 */

import { DUAL_BACKEND_GRAPHICS_REQUIREMENTS } from '@/graphics/GraphicsRequirements'
import type { EngineContext } from '@/runtime/EngineContext'
import type { Plugin } from '@/shared/types/plugin'
import type { ViewRegistry } from '@/shared/types/services'
import { PickingServiceImpl } from './PickingService'

/** 拾取插件：Phase 02 空壳服务，注册 PickingService 并提供 provider 注册点 */
export class PickingPlugin implements Plugin {
  /** 插件名称 */
  readonly name = 'picking'
  /** 插件启动优先级 */
  readonly priority = 25
  /** PickingService 本身只编排 provider；GPU 私有能力由 provider 请求边界校验。 */
  readonly graphicsRequirements = DUAL_BACKEND_GRAPHICS_REQUIREMENTS
  /** 依赖的服务名列表 */
  readonly dependencies = ['ViewRegistry'] as const
  /** 对外提供的服务名列表 */
  readonly provides = ['PickingService'] as const

  private context: EngineContext | null = null
  private viewRegistry: ViewRegistry | null = null
  private service: PickingServiceImpl | null = null
  private _disposed = false

  /**
   * 初始化插件，注册 PickingService。
   *
   * @param context - 引擎上下文
   */
  onInit(context: EngineContext): void {
    this.context = context
    this._disposed = false
    this.service = new PickingServiceImpl(
      () => {
        const { container } = context
        const rect = container.getBoundingClientRect()
        return {
          x: rect.left,
          y: rect.top,
          width: rect.width,
          height: rect.height,
        }
      },
      () => this.viewRegistry,
      () => context.getPixelRatio()
    )
    context.registerService('PickingService', this.service)
  }

  /** 启动插件，解析 ViewRegistry 依赖 */
  onStart(): void {
    if (!this.context) {
      return
    }

    this.viewRegistry = this.context.getService('ViewRegistry')
  }

  /** 销毁插件，释放 service */
  onDestroy(): void {
    if (this._disposed) return
    this._disposed = true
    this.service?.dispose()
    this.service = null
    this.viewRegistry = null
    this.context = null
  }
}
